import { FC, useState } from 'react'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import {
  DataGridPremium,
  GridColDef,
  GridPaginationModel,
  gridFilterModelSelector,
  gridFilteredTopLevelRowCountSelector,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import { useSystemMessagesHistoryRequest, useToggleSystemMessageVisibility } from 'src/hooks/api'
import { EntityHeader, HasSidebarLayout, TableToolbar } from 'src/components'
import { SystemMessageHistoryColumns } from 'src/components/columns/systemMessage'
import { SystemMessageHistory } from 'src/api'
import { DownloadingFilePayload, useAddPopupMessage, useDownloadingFile, useQueryParams } from 'src/hooks/ui'
import { SearchParamsKeys } from 'src/enums'
import { defaultRowsValuePerPage } from 'src/config'

export type ExtendedSystemMessageHistory = SystemMessageHistory & {
  downloadUsers?: {
    urlPath?: string
    queryParams?: Record<string, string>
  }
}

const columns = (
  downloadFile: (payload: DownloadingFilePayload) => void,
  onToggleVisibility: (id: number, isHidden: boolean) => void,
): GridColDef[] => [
  SystemMessageHistoryColumns.Author,
  SystemMessageHistoryColumns.Visibility,
  SystemMessageHistoryColumns.Status,
  SystemMessageHistoryColumns.Title,
  SystemMessageHistoryColumns.Message,
  SystemMessageHistoryColumns.MessageType,
  SystemMessageHistoryColumns.TransportType,
  SystemMessageHistoryColumns.MessageAudience,
  SystemMessageHistoryColumns.CustomParams,
  SystemMessageHistoryColumns.Recipients(downloadFile),
  SystemMessageHistoryColumns.Action(onToggleVisibility),
]
const defaultPerPage = 50
const defaultPage = 1
const defaultRowsPerPageOptions = defaultRowsValuePerPage
const initialQueryParams = {
  page: `${defaultPage}`,
  rowsPerPage: `${defaultPerPage}`,
}

export const MessageHistory: FC = () => {
  document.title = 'System Messages History'
  const [queryParams, setQueryParams] = useQueryParams(initialQueryParams)
  const apiRef = useGridApiRef()
  const { data, isInitialLoading } = useSystemMessagesHistoryRequest({
    params: queryParams,
  })
  const [rowCountState, setRowCountState] = useState(data?.count || 0)
  const { downloadFile } = useDownloadingFile()
  const addMessage = useAddPopupMessage()
  const [pendingToggle, setPendingToggle] = useState<{ id: number; isHidden: boolean } | null>(null)
  const toggleVisibility = useToggleSystemMessageVisibility({
    options: {
      onError: ({ message: text }) => addMessage({ text, type: 'error' }),
    },
  })
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const history = data?.rows || []
  const extendedHistory: ExtendedSystemMessageHistory[] = history.map((item) => {
    return {
      ...item,
      downloadUsers: {
        urlPath: `v1/admin/system-messages/history/${item.id}/export-users`,
        ueryParams: { timeZone: clientTimeZone },
      },
    }
  })
  const page = queryParams["page"] ? parseInt(queryParams["page"], 10) - 1 : defaultPage - 1
  const pageSize = parseInt(queryParams["rowsPerPage"], 10) || defaultPerPage

  const handleChangePaginationModel = async (pagination: GridPaginationModel): Promise<void> => {
    const newPage = pagination.page + 1
    setQueryParams({
      [SearchParamsKeys.Page]: `${newPage}`,
      [SearchParamsKeys.RowsPerPage]: `${pagination.pageSize}`,
    }, [
      SearchParamsKeys.Page,
      SearchParamsKeys.RowsPerPage,
    ])
  }

  const handleChangeState = async () => {
    const filterModel = gridFilterModelSelector(apiRef.current.state)
    if (filterModel.items.length > 0 && filterModel.items[0].value) {
      const visibleRowCount = gridFilteredTopLevelRowCountSelector(apiRef.current.state)
      if (visibleRowCount) setRowCountState(visibleRowCount)
    }
    else setRowCountState(data?.count || 0)
  }

  const handleToggleVisibility = (id: number, isHidden: boolean) => {
    setPendingToggle({ id, isHidden })
  }

  const handleConfirmToggle = () => {
    if (!pendingToggle) return
    toggleVisibility.mutate(
      { id: pendingToggle.id, isHidden: !pendingToggle.isHidden },
      { onSettled: () => setPendingToggle(null) },
    )
  }

  return (
    <>
    <HasSidebarLayout>
      <Grid container flexDirection="column">
        <Grid item>
          <EntityHeader
            title="Message History"
          />
        </Grid>
        <Grid item width="100%">
          <Paper sx={{ flexGrow: 1, minHeight: '200px', height: '100%' }}>
            <DataGridPremium<ExtendedSystemMessageHistory>
              apiRef={apiRef}
              pagination
              autoHeight={false}
              disableRowSelectionOnClick
              rows={extendedHistory}
              columns={columns(downloadFile, handleToggleVisibility)}
              rowCount={rowCountState}
              loading={isInitialLoading}
              getRowHeight={() => 'auto'}
              paginationModel={{ page, pageSize }}
              rowThreshold={2}
              columnThreshold={2}
              paginationMode="server"
              pageSizeOptions={defaultRowsPerPageOptions}
              onPaginationModelChange={handleChangePaginationModel}
              onStateChange={handleChangeState}
              localeText={{
                columnMenuSortAsc: "Sort A-Z",
                columnMenuSortDesc: "Sort Z-A",
              }}
              slots={{
                toolbar: () => <TableToolbar />
              }}
              sx={(theme) => ({
                border: "none",
                "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                  paddingLeft: "5px",
                  overflow: "visible",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  whiteSpace: "normal",
                  lineHeight: 1,
                  fontSize: "13px",
                  fontWeight: 400,
                  color: "rgba(0, 0, 0, 0.6)",
                  overflow: "visible",
                },
              })}
            />
          </Paper>
        </Grid>
      </Grid>
    </HasSidebarLayout>
    <Dialog open={Boolean(pendingToggle)} onClose={() => !toggleVisibility.isLoading && setPendingToggle(null)}>
      <DialogTitle>
        {pendingToggle?.isHidden ? 'Show message?' : 'Hide message?'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {pendingToggle?.isHidden
            ? 'This message will become visible in users notifications.'
            : 'This message will be hidden from users notifications.'}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPendingToggle(null)}>Cancel</Button>
        <Button
          onClick={handleConfirmToggle}
          color={pendingToggle?.isHidden ? 'primary' : 'error'}
          variant="contained"
          disabled={toggleVisibility.isLoading}
        >
          {pendingToggle?.isHidden ? 'Show' : 'Hide'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  )
}

export default MessageHistory
